import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { BRL, RangePicker, KpiGrid, PeriodChart, PeriodTable } from "./AdAnalyticsView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const num = (v) => Number(v || 0).toLocaleString("pt-BR");

/** Consolidated analytics of all the user's ads ("Resultados" tab on /ads). */
export default function AdsResultsTab({ onOpenAd }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/ads/analytics/summary`, { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar resultados"))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-3" data-testid="ads-results-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-bold tracking-tight">Resultados de todos os anúncios</h2>
          <p className="text-xs text-muted-foreground">Impressões, alcance, cliques, CTR, custo por clique, gasto e conversões.</p>
        </div>
        <RangePicker days={days} onChange={setDays} testPrefix="ads-results-range" />
      </div>
      {loading && <p className="text-sm text-muted-foreground py-6">Carregando...</p>}
      {!loading && data && (
        <>
          <KpiGrid totals={data.totals} testPrefix="ads-results" />
          <PeriodChart series={data.series} testPrefix="ads-results" />
          <PeriodTable series={data.series} testPrefix="ads-results" />
          {data.per_ad.length > 0 && (
            <Card className="p-3 overflow-x-auto" data-testid="ads-results-per-ad">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Por anúncio (período)</div>
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="text-left border-b border-border">
                    <th className="py-1.5 pr-2 font-medium">Anúncio</th>
                    <th className="py-1.5 pr-2 font-medium text-right">Impr.</th>
                    <th className="py-1.5 pr-2 font-medium text-right">Alcance</th>
                    <th className="py-1.5 pr-2 font-medium text-right">Cliques</th>
                    <th className="py-1.5 pr-2 font-medium text-right">CTR</th>
                    <th className="py-1.5 pr-2 font-medium text-right">CPC</th>
                    <th className="py-1.5 pr-2 font-medium text-right">Conv.</th>
                    <th className="py-1.5 font-medium text-right">Gasto</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_ad.map((a) => (
                    <tr key={a.ad_id} className="border-b border-border/60 last:border-0 tabular-nums hover:bg-muted/50 cursor-pointer" onClick={() => onOpenAd?.(a)} data-testid={`ads-results-row-${a.ad_id}`}>
                      <td className="py-1.5 pr-2 font-medium max-w-[180px] truncate">{a.title}</td>
                      <td className="py-1.5 pr-2 text-right">{num(a.impressions)}</td>
                      <td className="py-1.5 pr-2 text-right">{num(a.reach)}</td>
                      <td className="py-1.5 pr-2 text-right">{num(a.clicks)}</td>
                      <td className="py-1.5 pr-2 text-right">{Number(a.ctr).toFixed(2)}%</td>
                      <td className="py-1.5 pr-2 text-right">{BRL(a.cpc)}</td>
                      <td className="py-1.5 pr-2 text-right">{num(a.conversions)}</td>
                      <td className="py-1.5 text-right">{BRL(a.spent_brl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
